import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  useToast 
} from '@chakra-ui/react';
import { getVideoData, updateVideoData } from '../../../services/Videos';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Section4 = () => {

  const { alias } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'description', 'scene_description', 'provenance', 'square_inch_rate_in_inr_terms', ];
    const rightFields = ['auction_code','lot_number', 'imca_lots_offered_quantity', 'imca_lots_sold_quantity', 'place_of_publication_creation', 'country_of_publication_creation', 'location_featured_in_artwork'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "description": {
        "type": "string",
        "title": "Description"
      },
      "scene_description": {
        "type": "string",
        "title": "Scene Description"
      },
      "provenance": {
        "type": "string",
        "title": "Provenance"
      },
      "square_inch_rate_in_inr_terms": {
        "type": "string",
        "title": "Square Inch Rate in INR Terms"
      },

    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "auction_code": {
        "type": "string",
        "title": "Auction Code"
      },      
      "lot_number": {
        "type": "string",
        "title": "Lot Number"
      },
      "imca_lots_offered_quantity": {
        "type": "string",
        "title": "IMCA Lots Offered Quantity"
      },
      "imca_lots_sold_quantity": {
        "type": "string",
        "title": "IMCA Lots Sold Quantity"
      },
      "place_of_publication_creation": {
        "type": "string",
        "title": "Place of Publication/Creation"
      },
      "country_of_publication_creation": {
        "type": "string",
        "title": "Country of Publication/Creation"
      },
      "location_featured_in_artwork": {
        "type": "string",
        "title": "Location Featured in Artwork"
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Quadrant-4 fields",
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "description": {
        "type": "string",
        "title": "Description"
      },
      "scene_description": {
        "type": "string",
        "title": "Scene Description"
      },
      "provenance": {
        "type": "string",
        "title": "Provenance"
      },
      "square_inch_rate_in_inr_terms": {
        "type": "string",
        "title": "Square Inch Rate in INR Terms"
      },
      "auction_code": {
        "type": "string",
        "title": "Auction Code"
      },      
      "lot_number": {
        "type": "string",
        "title": "Lot Number"
      },
      "imca_lots_offered_quantity": {
        "type": "string",
        "title": "IMCA Lots Offered Quantity"
      },
      "imca_lots_sold_quantity": {
        "type": "string",
        "title": "IMCA Lots Sold Quantity"
      },
      "place_of_publication_creation": {
        "type": "string",
        "title": "Place of Publication/Creation"
      },
      "country_of_publication_creation": {
        "type": "string",
        "title": "Country of Publication/Creation"
      },
      "location_featured_in_artwork": {
        "type": "string",
        "title": "Location Featured in Artwork"
      }
    }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "description": {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },
    "scene_description": {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },    
    "provenance": {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },
    "square_inch_rate_in_inr_terms": {
      "ui:emptyValue": ""
    }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
    "lot_number": {
      "ui:emptyValue": ""
    },
    "auction_code": {
      "ui:emptyValue": ""
    },
    "imca_lots_offered_quantity": {
      "ui:emptyValue": ""
    },
    "imca_lots_sold_quantity": {
      "ui:emptyValue": ""
    },
    "place_of_publication_creation": {
      "ui:emptyValue": ""
    },
    "country_of_publication_creation": {
      "ui:emptyValue": ""
    },
    "location_featured_in_artwork": {
      "ui:emptyValue": ""
    }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "description": {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },
    
    "provenance": {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },
    "square_inch_rate_in_inr_terms": {
      "ui:emptyValue": ""
    },
    "lot_number": {
      "ui:emptyValue": ""
    },
    "place_of_publication_creation": {
      "ui:emptyValue": ""
    },
    "country_of_publication_creation": {
      "ui:emptyValue": ""
    },
    "location_featured_in_artwork": {
      "ui:emptyValue": ""
    }
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getVideoData(alias, 'section-4');
          if (result) {
            const data = result?.data?.[0] || {};
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateVideoData(alias, finalFormData);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Videos Quadrant-4 Fields</Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Videos Quadrant-4 Fields</Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section4;